import React, {
  useRef,
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subYears,
} from "date-fns";
import {
  Account,
  AccountsApiSelectGroupsIdRequest,
  CategoryTree,
  CategoryV2,
  PublicAccount,
  useCategoryTreeV2,
  useListAndSearchAllCategoriesV2,
} from "@bn/api/apiv1";
import { keepPreviousData } from "@tanstack/react-query";
import { useRangeSelectControl } from "@hooks";
import { BarChartMode } from "@components";
import { intervalToTimeRange } from "@helpers";
import { useSelectGroupsForId } from "../hooks/useSelectGroupsForId";

const generateNewRangeByType = (type: IntervalType) => {
  return type === "YTD"
    ? {
        startDate: startOfYear(new Date()),
        endDate: endOfYear(new Date()),
      }
    : type === "all"
      ? {
          startDate: subYears(startOfYear(new Date()), 5),
          endDate: new Date(),
        }
      : (intervalToTimeRange(type) as Required<RangeType>);
};

const disabledIntervalsForMonhtly: IntervalType[] = ["threeYears", "all"];
const disabledIntervalsForYearly: IntervalType[] = ["YTD", "oneYear"];

const selectedGroupParams: AccountsApiSelectGroupsIdRequest = {
  id: "householdBook",
  fallbackAccountType: ["02_cash"],
  excludeManuallyMaintainedFallbackAccounts: true,
};

interface CashflowAnalyzerContextProps {
  barChartRef: React.RefObject<{
    resetActiveItem: () => void;
  } | null>;
  intervalType: IntervalType;
  selectInterval: (value: IntervalType) => void;
  isCustomRangeSelectOpened?: boolean;
  mainPeriod: Required<RangeType>;
  setMainPeriod: Dispatch<SetStateAction<Required<RangeType>>>;
  subPeriod: Required<RangeType>;
  setSubPeriod: Dispatch<SetStateAction<Required<RangeType>>>;
  mode: BarChartMode;
  setMode: Dispatch<SetStateAction<BarChartMode>>;
  onModeChange: (type: BarChartMode) => void;
  accountsBalance: number;
  refetchGroups: () => void;
  selectedAccounts?: number[];
  accounts?: (Account | PublicAccount)[];
  disabledIntervals: IntervalType[];
  categories?: CategoryV2[];
  categoryTree?: CategoryTree[];
  isCategoryTreeLoading: boolean;
  isLoadingAccounts: boolean;
}

const CashflowAnalyzerContext = createContext<
  CashflowAnalyzerContextProps | undefined
>(undefined);

export const useCashflowAnalyzerContext = (): CashflowAnalyzerContextProps => {
  const context = useContext(CashflowAnalyzerContext);
  if (!context) {
    throw new Error(
      "useCashflowAnalyzer must be used within a CashflowAnalyzerProvider"
    );
  }
  return context;
};

export const CashflowAnalyzerProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const [mode, setMode] = React.useState<BarChartMode>("monthly");
  const { intervalType, selectInterval } = useRangeSelectControl({
    id: `cashflowAnalyzer-${mode}`,
    onSelect: () => {},
    firstDate: new Date(),
  });

  const [mainPeriod, setMainPeriod] = useState<Required<RangeType>>(
    generateNewRangeByType(intervalType)
  );

  const [subPeriod, setSubPeriod] = useState<Required<RangeType>>({
    startDate: startOfMonth(new Date()),
    endDate: endOfMonth(new Date()),
  });
  const [disabledIntervals, setDisabledIntervals] = useState<IntervalType[]>([
    "threeYears",
    "all",
  ]);

  const {
    data: selectGroup,
    isFetching: isLoadingSelectedGroup,
    refetch: refetchGroups,
    selectedAccounts,
  } = useSelectGroupsForId({
    id: selectedGroupParams.id,
    accountParams: {
      type: ["02_cash"],
    },
    refetchOnWindowFocus: false,
  });

  const barChartRef = useRef<{
    resetActiveItem: () => void;
  }>(null);

  const accountsBalance = selectedAccounts?.reduce(
    (acc, account) => acc + (account.balance ?? 0),
    0
  );

  const checkSelectionValidity = useCallback(
    (type: BarChartMode) => {
      if (type === "monthly") {
        if (disabledIntervalsForMonhtly.includes(intervalType)) {
          selectInterval("YTD");
          const range = generateNewRangeByType("YTD");
          setMainPeriod({
            startDate: range.startDate,
            endDate: range.endDate,
          });
        }
      }
      if (type === "yearly") {
        if (disabledIntervalsForYearly.includes(intervalType)) {
          selectInterval("threeYears");
          const range = generateNewRangeByType("threeYears");
          setMainPeriod({
            startDate: range.startDate,
            endDate: range.endDate,
          });
        }
      }
    },
    [intervalType, selectInterval]
  );

  const onModeChange = (type: BarChartMode) => {
    if (type === "monthly") {
      setDisabledIntervals(disabledIntervalsForMonhtly);
      checkSelectionValidity(type);
    } else if (type === "yearly") {
      setDisabledIntervals(disabledIntervalsForYearly);
      checkSelectionValidity(type);
    } else {
      setDisabledIntervals([]);
    }
    barChartRef.current?.resetActiveItem();
    setMode(type);
  };

  useEffect(() => {
    checkSelectionValidity(mode);
  }, [checkSelectionValidity, mode]);

  const onIntervalTypeChange = (type: IntervalType) => {
    if (intervalType === type) return;

    selectInterval(type);
    barChartRef.current?.resetActiveItem();

    const intervalRange = generateNewRangeByType(type);
    setMainPeriod({
      startDate: intervalRange.startDate,
      endDate: intervalRange.endDate,
    });
    setSubPeriod({
      startDate: intervalRange.startDate,
      endDate: intervalRange.endDate,
    });
  };

  const { data: categories } = useListAndSearchAllCategoriesV2(undefined, {
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const { data, isLoading: isCategoryTreeLoading } = useCategoryTreeV2(
    {
      accountId: selectGroup?.accountIds,
      minDate: format(subPeriod.startDate, "yyyy-MM-dd"),
      maxDate: format(subPeriod.endDate, "yyyy-MM-dd"),
    },
    {
      staleTime: 0,
      refetchOnWindowFocus: false,
      enabled: Boolean(selectGroup?.accountIds?.length),
    }
  );

  return (
    <CashflowAnalyzerContext.Provider
      value={{
        barChartRef,
        intervalType,
        selectInterval: onIntervalTypeChange,
        mainPeriod,
        setMainPeriod,
        subPeriod,
        setSubPeriod,
        mode,
        setMode,
        onModeChange,
        selectedAccounts: selectGroup?.accountIds,
        accountsBalance,
        accounts: selectedAccounts,
        isCategoryTreeLoading,
        categoryTree: data?.categoryTrees,
        categories: categories?.categories,
        refetchGroups,
        disabledIntervals,
        isLoadingAccounts: isLoadingSelectedGroup,
      }}
    >
      {children}
    </CashflowAnalyzerContext.Provider>
  );
};
